// -----------------------------------------------------------------------------
// shared/geometry.ts — small 2D layout helpers for the compositor.
//
// Tier 0 only needs to place a source image/video inside the project frame with
// a "contain" (letterbox) or "cover" (fill, crop edges) fit. Full transform
// matrices for PiP/crop/rotate arrive in Tier 1; this stays intentionally tiny.
// -----------------------------------------------------------------------------

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

// Scale (srcW × srcH) to fit inside (dstW × dstH) preserving aspect ratio, then
// centre it. Leaves letterbox/pillarbox bars — nothing is cropped.
export function containRect(srcW: number, srcH: number, dstW: number, dstH: number): Rect {
  if (srcW <= 0 || srcH <= 0) return { x: 0, y: 0, w: dstW, h: dstH };
  const scale = Math.min(dstW / srcW, dstH / srcH);
  const w = srcW * scale;
  const h = srcH * scale;
  return { x: (dstW - w) / 2, y: (dstH - h) / 2, w, h };
}

// Scale to COVER the destination preserving aspect ratio, centring; edges that
// overflow are clipped by the frame. (Not used by the default Tier 0 draw, but
// available for the "fill" fit mode.)
export function coverRect(srcW: number, srcH: number, dstW: number, dstH: number): Rect {
  if (srcW <= 0 || srcH <= 0) return { x: 0, y: 0, w: dstW, h: dstH };
  const scale = Math.max(dstW / srcW, dstH / srcH);
  const w = srcW * scale;
  const h = srcH * scale;
  return { x: (dstW - w) / 2, y: (dstH - h) / 2, w, h };
}
